import { NavLink, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { OrionLockup, OrionMark } from '@/components/brand/orion-logo';
import { Tooltip } from '@/components/ui/misc';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { usePreferences } from '@/lib/preferences';
import { NAVIGATION } from '@/lib/registry/navigation';
import { cn } from '@/lib/utils';

interface SidebarProps {
  /** Live counters keyed by the `badge` field in the navigation registry. */
  badges?: Partial<Record<'renewals' | 'overdueReminders', number>>;
  onNavigate?: () => void;
  /**
   * The mobile drawer passes false: a full-screen sheet has nothing to collapse into, and the
   * desktop's persisted collapsed state must never leak in — an icon-only rail inside a
   * full-width sheet reads as a broken menu, not a compact one.
   */
  collapsible?: boolean;
  /** Renders an X in the header — the full-screen drawer has no "outside" left to tap. */
  onClose?: () => void;
  className?: string;
}

/**
 * The rail.
 *
 * Weight is carried by ink, not by fills. A resting item is muted text on the navy with
 * no background at all; hover adds a barely-there wash; the active item gets a soft
 * panel, white text, and a 2.5px blue bar at its leading edge. That bar is the only
 * saturated pixel in the whole rail — which is precisely why it works as a position
 * indicator, and why the surrounding chrome can stay quiet.
 *
 * The bar is also the reason the active state does not need to shout: it is a
 * non-colour cue (position + shape), so the state survives colour-blindness and reads
 * instantly in the collapsed rail where there is no label to fall back on.
 */
export function Sidebar({ badges, onNavigate, collapsible = true, onClose, className }: SidebarProps) {
  const { t } = useI18n();
  const { can } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = usePreferences();
  const location = useLocation();

  const collapsed = collapsible && sidebarCollapsed;

  return (
    <aside
      className={cn(
        'bg-sidebar text-sidebar-foreground border-sidebar-border flex h-full flex-col border-r',
        'transition-[width] duration-200 ease-[cubic-bezier(0.32,0.72,0,1)]',
        collapsed ? 'w-[4.25rem]' : 'w-[15.5rem]',
        className,
      )}
    >
      <div className={cn('flex h-14 shrink-0 items-center px-4', collapsed && 'justify-center px-0')}>
        {collapsed ? <OrionMark className="size-8" title="Orion Insurance OS" /> : <OrionLockup size="sm" />}
        {onClose && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            className="text-sidebar-foreground/70 hover:bg-sidebar-hover hover:text-sidebar-accent-foreground ml-auto"
            aria-label={t('action.close')}
          >
            <X />
          </Button>
        )}
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-2.5 pt-2 pb-4">
        {NAVIGATION.map((group) => {
          const visible = group.items.filter((item) => !item.permission || can(item.permission));
          if (!visible.length) return null;

          return (
            <div key={group.labelKey}>
              {/* Collapsed, the eyebrow becomes a hairline: the grouping is still
                  communicated, but a 3-letter truncation of a Greek word is not. */}
              {collapsed ? (
                <div className="bg-sidebar-border mx-auto mb-2 h-px w-6" aria-hidden />
              ) : (
                <p className="text-sidebar-foreground/45 px-2.5 pb-2 text-[10.5px] font-semibold tracking-[0.08em] uppercase">
                  {t(group.labelKey)}
                </p>
              )}

              <ul className="space-y-px">
                {visible.map((item) => {
                  const badgeCount = item.badge ? badges?.[item.badge] : undefined;
                  const hasBadge = badgeCount !== undefined && badgeCount > 0;
                  const isAlarm = item.badgeTone === 'alarm';
                  const isActive = item.matchPrefix
                    ? location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
                    : location.pathname === item.to;

                  const link = (
                    <NavLink
                      to={item.to}
                      onClick={onNavigate}
                      className={cn(
                        'group relative flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium',
                        'transition-[background-color,color] duration-150',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'hover:bg-sidebar-hover hover:text-sidebar-accent-foreground',
                        collapsed && 'justify-center px-0',
                      )}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {isActive && (
                        <span
                          className="bg-primary absolute top-1/2 left-0 h-4 w-[2.5px] -translate-y-1/2 rounded-r-full"
                          aria-hidden
                        />
                      )}

                      <item.icon
                        className={cn(
                          'size-[17px] shrink-0 transition-opacity duration-150',
                          isActive ? 'opacity-100' : 'opacity-60 group-hover:opacity-90',
                        )}
                      />

                      {!collapsed && <span className="flex-1 truncate">{t(item.labelKey)}</span>}

                      {!collapsed && hasBadge && (
                        <span
                          className={cn(
                            'tabular rounded px-1.5 py-px text-[10.5px] font-semibold',
                            isAlarm
                              ? 'bg-[var(--sidebar-alarm-bg)] text-[var(--sidebar-alarm-fg)]'
                              : 'text-sidebar-foreground/70 bg-white/[0.07]',
                          )}
                        >
                          {badgeCount}
                        </span>
                      )}

                      {collapsed && hasBadge && (
                        <span
                          className={cn(
                            'absolute top-1.5 right-3 size-1.5 rounded-full',
                            isAlarm ? 'bg-critical' : 'bg-sidebar-foreground/50',
                          )}
                          aria-hidden
                        />
                      )}
                    </NavLink>
                  );

                  return (
                    <li key={item.to}>
                      {collapsed ? (
                        <Tooltip content={t(item.labelKey)} side="right">
                          {link}
                        </Tooltip>
                      ) : (
                        link
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {collapsible && (
        <div className="border-sidebar-border shrink-0 border-t p-2">
          <Button
            variant="ghost"
            size={collapsed ? 'icon-sm' : 'sm'}
            onClick={toggleSidebar}
            className={cn(
              'text-sidebar-foreground/70 hover:bg-sidebar-hover hover:text-sidebar-accent-foreground w-full justify-start',
              collapsed && 'mx-auto w-auto justify-center',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            {!collapsed && <span>{t('nav.collapse')}</span>}
          </Button>
        </div>
      )}
    </aside>
  );
}
